import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	EmailProgramDraft,
	emailProgramService,
} from "../../services/emailProgram.service";

export const useEmailPrograms = () =>
	useQuery({
		queryKey: ["sims-email-programs"],
		queryFn: () => emailProgramService.listEmailPrograms(),
		staleTime: 1000 * 60 * 5,
	});

/**
 * The rendered email for one account.
 *
 * 🔴 `brandingAccountId` is IN THE KEY, and has to stay there. It is an input to
 * the query function, so leaving it out would serve the previously selected
 * account's document from cache while the selector showed a different name —
 * the failure would look like "branding does not apply", which is exactly the
 * thing this page exists to disprove.
 *
 * SIMS-9056: so is `draft`, for the same reason — an unsaved edit renders a
 * different document, and the preview must be the one on screen. The previous
 * render stays up while the next one loads, so typing does not flash a spinner.
 */
export const useEmailProgramPreview = (
	key: string,
	brandingAccountId: string,
	enabled: boolean,
	draft?: EmailProgramDraft,
) =>
	useQuery({
		queryKey: [
			"sims-email-program-preview",
			key,
			brandingAccountId,
			draft ?? null,
		],
		queryFn: () =>
			emailProgramService.previewEmailProgram(key, brandingAccountId, draft),
		enabled: enabled && !!key && !!brandingAccountId,
		placeholderData: keepPreviousData,
	});

export const useSendTestEmail = () =>
	useMutation({
		mutationFn: ({
			key,
			email,
			brandingAccountId,
			draft,
		}: {
			key: string;
			email: string;
			brandingAccountId?: string;
			draft?: EmailProgramDraft;
		}) =>
			emailProgramService.sendTestEmail(key, email, brandingAccountId, draft),
	});

/**
 * SIMS-9056. One program's configuration at one scope. An empty
 * `scopeAccountId` is the configuration every account gets — in the key as
 * `null`, so it can never be served as some account's override.
 */
export const useEmailProgramConfig = (key: string, scopeAccountId?: string) =>
	useQuery({
		queryKey: ["sims-email-program-config", key, scopeAccountId || null],
		queryFn: () =>
			emailProgramService.getEmailProgramConfig(
				key,
				scopeAccountId || undefined,
			),
		enabled: !!key,
		// The editor seeds its fields from this; a refetch on focus would only
		// ever replace them with the same values, or race an edit in progress.
		refetchOnWindowFocus: false,
	});

/**
 * What a save or a reset can change: the program's configuration at any scope
 * (an all-accounts edit changes what every account inherits), every rendered
 * preview, and the subjects in the list. Invalidated by PREFIX, which covers
 * every key underneath it.
 */
const invalidateEmailPrograms = (
	queryClient: ReturnType<typeof useQueryClient>,
): Promise<unknown> =>
	Promise.all([
		queryClient.invalidateQueries({ queryKey: ["sims-email-program-config"] }),
		queryClient.invalidateQueries({ queryKey: ["sims-email-program-preview"] }),
		queryClient.invalidateQueries({ queryKey: ["sims-email-programs"] }),
	]);

export const useSaveEmailProgramConfig = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			key,
			scopeAccountId,
			input,
		}: {
			key: string;
			scopeAccountId?: string;
			input: EmailProgramDraft;
		}) =>
			emailProgramService.saveEmailProgramConfig(
				key,
				scopeAccountId || undefined,
				input,
			),
		onSuccess: () => invalidateEmailPrograms(queryClient),
	});
};

export const useResetEmailProgramConfig = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({
			key,
			scopeAccountId,
		}: {
			key: string;
			scopeAccountId?: string;
		}) =>
			emailProgramService.resetEmailProgramConfig(
				key,
				scopeAccountId || undefined,
			),
		onSuccess: () => invalidateEmailPrograms(queryClient),
	});
};
